import type {AuthorizedFetch} from "./client";

export type ExecutionRunStatus =
  | "QUEUED" | "RUNNING" | "VALIDATING" | "AWAITING_HUMAN_REVIEW"
  | "COMPLETED" | "COMPLETED_WITH_WARNINGS" | "BLOCKED" | "FAILED" | "CANCELLED";

export type ExecutionReviewStatus =
  | "NOT_REQUIRED" | "PENDING" | "APPROVED" | "REJECTED" | "REVISION_REQUIRED";

export type ExecutionPartStatus =
  | "PENDING" | "QUEUED" | "PREFLIGHT" | "RUNNING" | "VALIDATING"
  | "COMPLETED" | "COMPLETED_WITH_WARNINGS" | "BLOCKED" | "FAILED"
  | "CANCELLED" | "SKIPPED";

export interface ExecutionProgress {
  totalBatches: number;
  completedBatches: number;
  totalParts: number;
  completedParts: number;
  percent: number;
}

export interface ExecutionRun {
  id: string;
  tenantId: string;
  company: string;
  businessUnit: string;
  runKey: string;
  attemptNo: number;
  runType: "TASK_ANALYSIS" | "SOFTWARE_DELIVERY" | "BACKUP_RECOVERY" | "VALIDATION" | "MANUAL";
  title: string;
  summary: string;
  executionMode: "PROPOSAL_ONLY" | "SUPERVISED" | "OPERATIONAL";
  status: ExecutionRunStatus;
  humanReviewRequired: boolean;
  reviewStatus: ExecutionReviewStatus;
  cancelRequested: boolean;
  currentBatchKey: string;
  currentPartKey: string;
  metadata: Record<string, unknown>;
  progress: ExecutionProgress;
  version: number;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  lastHeartbeatAt: string | null;
}

export interface ExecutionPart {
  id: string;
  runId: string;
  batchId: string;
  batchKey: string;
  partKey: string;
  title: string;
  partType: string;
  ordinal: number;
  status: ExecutionPartStatus;
  subjectType: string;
  subjectId: string;
  message: string;
  metadata: Record<string, unknown>;
  metrics: Record<string, unknown>;
  result: Record<string, unknown>;
  failureCode: string;
  failureMessage: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface ExecutionBatch {
  id: string;
  runId: string;
  batchKey: string;
  title: string;
  ordinal: number;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  parts: ExecutionPart[];
}

export interface ExecutionRunDetail {
  run: ExecutionRun;
  batches: ExecutionBatch[];
}

export interface ExecutionEvent {
  id: string;
  runId: string;
  sequence: number;
  batchKey: string;
  partKey: string;
  eventType: string;
  status: string;
  data: Record<string, unknown>;
  actorSubject: string;
  correlationId: string;
  occurredAt: string;
}

export interface ExecutionEvidenceReference {
  id: string;
  runId: string;
  batchKey: string;
  partKey: string;
  evidenceType: string;
  sourceType: string;
  sourceReference: string;
  mediaType: string;
  sha256: string;
  byteLength: number;
  attributes: Record<string, unknown>;
  actorSubject: string;
  correlationId: string;
  recordedAt: string;
}

export interface ExecutionReview {
  id: string;
  runId: string;
  exactRunVersion: number;
  decision: "APPROVE" | "REJECT" | "REVISION_REQUIRED";
  rationale: string;
  reviewerSubject: string;
  actorSnapshot: Record<string, unknown>;
  correlationId: string;
  reviewedAt: string;
}

export interface ExecutionProgressAxis {
  completed: number;
  total: number;
  percent: number;
}

export interface ExecutionPartProjection {
  partId: string;
  batchKey: string;
  partKey: string;
  status: ExecutionPartStatus;
  eligible: boolean;
  blockerReason: string;
  governingRule: string;
  workerKey: string;
  modelKey: string;
  currentAttempt: number;
  maxAttempts: number;
  promptTokens: number | null;
  contextWindow: number | null;
  truncationPermitted: boolean;
  updatedAt: string;
}

export interface ExecutionAttempt {
  id: string;
  runId: string;
  partId: string;
  attemptNo: number;
  outcome: string;
  failureCode: string;
  failureMessage: string;
  actorSubject: string;
  correlationId: string;
  recordedAt: string;
}

export interface ExecutionRunProjection {
  runId: string;
  status: ExecutionRunStatus;
  currentActivity: string;
  nextEligibleWork: string;
  blockerReason: string;
  governingRule: string;
  humanGate: boolean;
  engineering: ExecutionProgressAxis;
  testing: ExecutionProgressAxis;
  evidence: ExecutionProgressAxis;
  humanGateProgress: ExecutionProgressAxis;
  parts: ExecutionPartProjection[];
  retryHistory: ExecutionAttempt[];
  completionSnapshotPresent: boolean;
}

export const listExecutionRuns = (
  fetcher: AuthorizedFetch,
  signal?: AbortSignal
) => read<ExecutionRun[]>(fetcher, "/api/v1/execution-center/runs", signal);

export const readExecutionRun = (
  fetcher: AuthorizedFetch,
  id: string,
  signal?: AbortSignal
) => read<ExecutionRunDetail>(fetcher,
  `/api/v1/execution-center/runs/${encodeURIComponent(id)}`, signal);

export const listExecutionEvents = (
  fetcher: AuthorizedFetch,
  id: string,
  signal?: AbortSignal
) => read<ExecutionEvent[]>(fetcher,
  `/api/v1/execution-center/runs/${encodeURIComponent(id)}/events`, signal);

export const listExecutionEvidence = (
  fetcher: AuthorizedFetch,
  id: string,
  signal?: AbortSignal
) => read<ExecutionEvidenceReference[]>(fetcher,
  `/api/v1/execution-center/runs/${encodeURIComponent(id)}/evidence`, signal);

export const listExecutionReviews = (
  fetcher: AuthorizedFetch,
  id: string,
  signal?: AbortSignal
) => read<ExecutionReview[]>(fetcher,
  `/api/v1/execution-center/runs/${encodeURIComponent(id)}/reviews`, signal);

export const readExecutionProjection = async (
  fetcher: AuthorizedFetch,
  id: string,
  signal?: AbortSignal
): Promise<ExecutionRunProjection | null> => {
  const response = await fetcher(
    `/api/v1/execution-center/runs/${encodeURIComponent(id)}/orchestration/projection`,
    {signal, headers: {Accept: "application/json"}}
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as {message?: string} | null;
    throw new Error(problem?.message ?? `Execution projection request failed: ${response.status}`);
  }
  return await response.json() as ExecutionRunProjection;
};

async function read<T>(
  fetcher: AuthorizedFetch,
  path: string,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetcher(path, {
    signal,
    headers: {Accept: "application/json"}
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as {message?: string} | null;
    throw new Error(problem?.message ?? `Execution Center request failed: ${response.status}`);
  }
  return await response.json() as T;
}
